import { JSON, JsonElementType, JsonObject, JsonProperty } from "ta-json";

import Guard from "../../guard";
import { TypeGuards } from "../../type-guards";
import { ILoadOption } from "./load-option";
import { LoadOption } from "./load-options";
import { IRelationSpecification, RelationSpecification } from "./relation-specification";

/**
 * Specifies which relations to load.
 */
export interface IRelationLoadOption extends ILoadOption {
    /**
     * Gets a list of relations that need to be loaded.
     * This is only useful when {@link LoadOption} is {@link LoadOption.Custom}.
     */
    relations: Array<IRelationSpecification>;

    /**
     * Can limit the number of related items that are returned.
     */
    maxRelatedItems?: number;

    /**
     * Creates a deep clone of this instance.
     */
    deepClone(): IRelationLoadOption;
}

@JsonObject()
export class RelationLoadOption implements IRelationLoadOption {
    /**
     * Load no relations.
     */
    static readonly None: Readonly<IRelationLoadOption> = new RelationLoadOption(LoadOption.None);

    /**
     * Load all relations.
     */
    static readonly All: Readonly<IRelationLoadOption> = new RelationLoadOption(LoadOption.All);

    @JsonProperty("load_option")
    loadOption: LoadOption = LoadOption.None;

    @JsonProperty("relations")
    @JsonElementType(RelationSpecification)
    relations: Array<IRelationSpecification> = [];

    @JsonProperty("max_related_items")
    maxRelatedItems?: number;

    constructor(param?: LoadOption | Array<string> | Array<IRelationSpecification> | RelationLoadOption) {
        if (param == null) {
            return;
        } else if (param instanceof RelationLoadOption) {
            this.loadOption = param.loadOption;
            this.relations = [...param.relations];
        } else if (param instanceof Array) {
            Guard.arrayNotEmpty(param);

            this.loadOption = LoadOption.Custom;

            if (TypeGuards.isStringArray(param)) {
                this.relations = param.map((relationName: string): IRelationSpecification => {
                    return new RelationSpecification(relationName);
                });
            } else {
                this.relations = [...param];
            }
        } else {
            this.loadOption = param;
        }
    }

    deepClone(): IRelationLoadOption {
        return JSON.deserialize<RelationLoadOption>(JSON.serialize(this), RelationLoadOption);
    }
}
